import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import _ from 'lodash';
import { toast } from '@chargebee/sting-react';
import {
  type FunnelAnswerField,
  type FunnelAnswerUpdate,
} from '@bbk/retention-sdk';
import {
  createDirectCancelQueue,
  createFunnelAnswerSession,
  createModalClosedEvents,
  createModalOpenedEvent,
  createNevermindQueue,
  createPageLoadedEvent,
  prepareFunnelEventQueue,
  type FunnelBackwardsAction,
  type FunnelEventQueue,
} from '@bbk/retention-sdk/internal';
import { FunnelLocalState } from '@bbk/retention-sdk/storage';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { collectForwards } from '@bbkAdminComponents/experiences/offers/collect-forwards';
import { type PlacementLossAversionPosition } from '@bbkAdminRedux/rtkq/placements.utils';
import { type RestExperience } from '@bbkAdminUtils/api-client/rest-experiences-api';
import {
  RestModalAction,
  RestModalActionsAccepts,
  RestModalTemplates,
  type RestModalCard,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import {
  Events as EventsClass,
  isDryRunPage,
  type NextStepHandler,
} from '@bbkFunnel/events';
import {
  createInitialSessionState,
  funnelSessionReducer,
  readDerivedModel,
  readSurvey,
  type FunnelAction,
  type FunnelDerivedCache,
  type FunnelSessionState,
  type FunnelSurvey,
} from '@bbkFunnel/pages/funnel-state';
import { FunnelView } from '@bbkFunnel/pages/funnel-view';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { CANCEL_WITH_REFUND_MODAL_ID } from '@bbkFunnelComponents/constants';
import {
  FunnelContext,
  isAdminFunnelSurface,
} from '@bbkFunnelComponents/FunnelContext';
import {
  type ModalHandleAnyInfo,
  type ModalOnClose,
} from '@bbkFunnelComponents/modal';
import { type CompetitorOtherTextHandler } from '@bbkFunnelComponents/modals/sections/competition-other-text';
import { type CompetitorOtherTextClearHandler } from '@bbkFunnelComponents/survey/competition/competition-content';
import { type ReasonOtherTextClearHandler } from '@bbkFunnelComponents/survey/reasons/reasons-content';
import { type ReasonOtherHandler } from '@bbkFunnelComponents/survey/reasons/reasons-other-text';
import {
  type DirectFromSurveyCancelHandler,
  type SurveyMultiRowResponseHandler,
} from '@bbkFunnelComponents/survey-multi-row';
import {
  createCancelCheckout,
  openCancelCheckout,
} from '@bbkFunnelUtils/cancel-checkout';
import { embedFont, prefetchImages } from '@bbkFunnelUtils/funnel-utils';

import '@bbkFunnelStylesheets/index.scss';

type Props = RestExperience & {
  disableThemeProvider?: boolean;
  isLossAversionLoading?: boolean;
  lossAversionErrors?: PlacementLossAversionPosition[];

  // Fully resolved theme; locale resolution happens at the entry points.
  emotionTheme: EmotionTheme;

  // Lifecycle props
  sendLCOEventsToParent?: CallableFunction;
  isLifecycle?: boolean;

  // Pricing table props
  country?: string;
};

/**
 * Named export for `funnel-view.tsx`. It takes this props object whole, as
 * `fullInfo`, because several funnel children read fields their own prop types
 * do not declare.
 */
export type FunnelProps = Props;

const Events = new EventsClass();

export type BackwardsObject = FunnelBackwardsAction;

type TrackEventHandler = (
  e: { target: { type: string } },
  value: string | boolean,
  key: FunnelAnswerField,
  /** Survey to report when committed state is not the snapshot being tracked. */
  trackedSurvey?: FunnelSurvey
) => void;

export type ExecuteEventsArg = FunnelEventQueue;

/** The `prevProps` / `prevState` the update effect stands in for. */
type PreviousCommit = {
  layoutCards: Props['layout']['cards'];
  modal: FunnelSessionState['modal'];
  showModal: boolean;
};

const Funnel = (props: Props) => {
  const context = useContext(FunnelContext);
  const { isPreview, isBrandingPage, isPricingPage } = context;

  /**
   * `defaultProps` resolved these before `this.props` was ever read, and the
   * resolved object is what reaches `funnel-view` as `fullInfo`, where children
   * dereference `company` without guarding. Destructuring defaults inside this
   * component would never reach them, so the object is rebuilt instead.
   */
  const fullInfo = useMemo<Props>(
    () => ({
      ...props,
      integrations: props.integrations ?? {},
      company: props.company ?? ({} as Props['company']),
    }),
    [props]
  );

  const [sessionState, setSessionState] = useState<FunnelSessionState>(
    createInitialSessionState
  );
  const [localState, setLocalState] = useState<FunnelLocalState>();

  const derivedCacheRef = useRef<FunnelDerivedCache>();
  const { model: derived, cache: derivedCache } = readDerivedModel(
    derivedCacheRef.current,
    fullInfo,
    isPreview
  );
  derivedCacheRef.current = derivedCache;

  /**
   * The class read `this.props`, `this.context`, `this.state` and `this.derived`
   * at the moment a handler ran, never at the moment it was created. Handlers
   * below read these refs to keep that, because several of them run long after
   * the render that produced them: the page-load timer fires after a configured
   * delay, and the queued callbacks run in a later commit.
   *
   * Render reads the values directly; only handlers go through the refs.
   */
  const propsRef = useRef(fullInfo);
  propsRef.current = fullInfo;
  const contextRef = useRef(context);
  contextRef.current = context;
  const derivedRef = useRef(derived);
  derivedRef.current = derived;

  const [answerSession] = useState(() =>
    createFunnelAnswerSession({
      initialAnswers: derived.survey,
      getTrackingContext: () => ({
        experience: propsRef.current,
        surveyType: derivedRef.current.surveyType,
        cardVersion:
          derivedRef.current.cardsByTemplate.get('multi_row_survey')?.version,
      }),
      dispatchEvent: (event) => {
        void executeEvents({ events: [event] });
      },
    })
  );
  const answers = useSyncExternalStore(
    answerSession.subscribe,
    answerSession.getAnswers
  );
  const survey = readSurvey(isPreview, derived.survey, answers);

  /**
   * Advanced synchronously by `dispatchSession` rather than mirrored from a
   * commit. Selecting and opening a modal can happen in one React event, and
   * the second reducer call must see the first one's output.
   */
  const sessionStateRef = useRef(sessionState);

  const pendingCallbacksRef = useRef<Array<() => void>>([]);
  const pageLoadModalTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const opaqueLoaderTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const checkoutInFlightRef = useRef(false);

  /**
   * The opaque-loader timer is scheduled after awaiting the event queue, so it
   * can be created once this component is already gone. Clearing on unmount
   * cannot reach a timer that does not exist yet, hence the flag.
   *
   * It tracks whether this component is unmounted right now, not whether it
   * ever was, so the mount effect resets it: a StrictMode remount reuses these
   * refs, and a latched flag would disable the loader for the rest of its life.
   */
  const unmountedRef = useRef(false);

  /**
   * One real page load must report one `page_loaded`. StrictMode replays the
   * mount effect against the same refs, so only this emission is guarded; the
   * effect around it has to replay in full to recreate the timer its cleanup
   * just cleared.
   */
  const pageLoadedRef = useRef(false);

  const isFirstCommitRef = useRef(true);
  const previousCommitRef = useRef<PreviousCommit>({
    layoutCards: fullInfo.layout.cards,
    modal: sessionState.modal,
    showModal: sessionState.showModal,
  });

  const dispatchSession = (action: FunnelAction) => {
    const produced = funnelSessionReducer(sessionStateRef.current, action);
    sessionStateRef.current = produced;
    setSessionState(produced);
  };

  // Keep the action's snapshot for its post-commit callback. A subsequent update
  // in the same event (such as clearing reason_other) must not change tracking.
  const updateAnswer = (
    update: FunnelAnswerUpdate,
    callback?: (produced: FunnelSurvey) => void
  ) => {
    const produced = answerSession.setAnswer(update);
    if (callback) pendingCallbacksRef.current.push(() => callback(produced));
  };

  const currentSurvey = () =>
    readSurvey(
      contextRef.current.isPreview,
      derivedRef.current.survey,
      answerSession.getAnswers()
    );

  const showRefundModalIfNeeded = (): boolean => {
    const { modal: currentModal, hasShownRefundModal } =
      sessionStateRef.current;
    const { layout } = propsRef.current;

    if (
      hasShownRefundModal ||
      currentModal?.id === CANCEL_WITH_REFUND_MODAL_ID
    ) {
      return false;
    }

    const refundModal = (layout?.cards || []).find(
      (card: any) =>
        card.id === CANCEL_WITH_REFUND_MODAL_ID &&
        card.template === RestModalTemplates.modal_refund_cancel
    );

    if (refundModal) {
      dispatchSession({
        type: 'showRefundModal',
        modal: refundModal as RestModalCard,
      });
      return true;
    }
    return false;
  };

  useEffect(() => {
    const restoreCheckout = (event: PageTransitionEvent) => {
      // The first pageshow is the initial page load. Only Back, which
      // restores this page, should unlock a checkout that already started.
      if (!event.persisted || !checkoutInFlightRef.current) {
        return;
      }
      checkoutInFlightRef.current = false;
      dispatchSession({ type: 'hideOpaqueLoader' });
    };
    window.addEventListener('pageshow', restoreCheckout);
    return () => window.removeEventListener('pageshow', restoreCheckout);
  }, []);

  const executeEvents = async (eventsAndSyncs: ExecuteEventsArg) => {
    const {
      isPreview: preview,
      isPricingPage: pricingPage,
      navigate,
    } = contextRef.current;
    const { synchronous = false } = eventsAndSyncs;
    const { submitConfig } = derivedRef.current;
    const { isLifecycle, sendLCOEventsToParent } = propsRef.current;

    const isCancelEvent = eventsAndSyncs.events?.some(
      (e) => e.event === 'cancel'
    );
    if (isCancelEvent && showRefundModalIfNeeded()) {
      return;
    }

    if (preview) {
      if (
        eventsAndSyncs.syncs?.length &&
        eventsAndSyncs.syncs.find(
          (s) => s.action === 'link' || s.action === 'confirm_cancel'
        )
      ) {
        BlackToastTrigger({ content: 'User would follow this action.' });
      }
      return;
    }
    const payload = prepareFunnelEventQueue(eventsAndSyncs, {
      isPricingPage: pricingPage,
      isLifecycle,
      config: submitConfig,
    });
    sendLCOEventsToParent?.(payload);
    const checkout = payload.syncs?.find((evt) => evt.isCheckoutProcessing);
    if (checkout) {
      if (
        isLifecycle ||
        pricingPage ||
        isAdminFunnelSurface(contextRef.current) ||
        isDryRunPage() ||
        checkoutInFlightRef.current
      ) {
        return;
      }
      checkoutInFlightRef.current = true;
      clearTimeout(opaqueLoaderTimerRef.current);
      dispatchSession({ type: 'showOpaqueLoader' });
      let navigating = false;
      try {
        const url = await createCancelCheckout(checkout);
        if (!unmountedRef.current) {
          navigating = openCancelCheckout(url, navigate);
        }
      } catch (error) {
        if (!unmountedRef.current) {
          toast({
            variant: 'danger',
            title:
              error instanceof Error
                ? error.message
                : 'Could not open checkout. Please try again.',
          });
        }
      } finally {
        checkoutInFlightRef.current = navigating;
        if (!navigating && !unmountedRef.current) {
          dispatchSession({ type: 'hideOpaqueLoader' });
        }
      }
      return;
    }
    // `navigate` is deliberately not part of `payload`: that object is
    // structured-cloned by `sendLCOEventsToParent`, and a function would make
    // the postMessage throw.
    if (synchronous) {
      dispatchSession({ type: 'showOpaqueLoader' });
      await Events.executeEventQueue({ ...payload, navigate });
      if (unmountedRef.current) {
        return;
      }
      const isRedirect = payload.syncs?.some(
        (s) => s.action === RestModalAction.link && _.isString(s.url)
      );
      if (!isRedirect) {
        clearTimeout(opaqueLoaderTimerRef.current);
        opaqueLoaderTimerRef.current = setTimeout(
          () => dispatchSession({ type: 'hideOpaqueLoader' }),
          2000
        );
      }
    } else {
      Events.executeEventQueue({ ...payload, navigate });
    }
  };

  // Track events for interaction with the survey
  const handleTrackEvent: TrackEventHandler = (
    e,
    value,
    key,
    trackedSurvey
  ) => {
    answerSession.trackAnswer(key, {
      answers: trackedSurvey ?? currentSurvey(),
      inputType: e.target.type,
      value,
    });
  };

  const handleBackwards = (action: BackwardsObject) => {
    executeEvents(
      createNevermindQueue({
        experience: propsRef.current,
        survey: currentSurvey(),
        action,
      })
    );
  };

  const updateCompetitionOtherText: CompetitorOtherTextHandler = (
    value,
    type = { type: 'change' }
  ) => {
    if (_.get(type, 'type') === 'change') {
      updateAnswer({
        field: 'competition_other',
        value,
      });
    } else if (_.get(type, 'type') === 'track') {
      handleTrackEvent(
        { target: { type: 'blur' } },
        value,
        'competition_other'
      );
    }
  };

  // Reasons for Leaving
  // Save the answers into that array
  const updateReasonsForLeaving = (
    name: string,
    value: string,
    tier: string,
    livesOn: string, // needs to be camelcase for sync event reasons
    onUpdated?: (produced: FunnelSurvey) => void
  ) => {
    updateAnswer(
      {
        field: 'reason_for_leaving',
        value: { name, value, tier, lives_on: livesOn },
      },
      onUpdated
    );
  };

  // As survey is filled out, values are stored via this
  // Only for multi-tier
  const handleResponseMulti: SurveyMultiRowResponseHandler = (
    e,
    value,
    tier,
    currentTier,
    type = { type: 'change' }
  ) => {
    const key = (e.target.name || tier)!;

    // if it's not competition,sentiment, feedback, confirmation
    // then it's a reason  for leaving
    // reason_for_leaving: [], // this is now an array of reasons
    //       competition: '',
    //       sentiment: null,
    //       feedback: '',
    //       confirmation: ''
    //
    // Every branch that tracks from a dispatch callback reports the state its
    // own action produced rather than reading the committed survey back out.
    // The two name the same value outside preview; in preview they diverge,
    // but `executeEvents` drops tracking events before emitting them, so
    // nothing observes the difference.
    if (key === 'feedback') {
      const typeVal = _.get(type, 'type');
      if (typeVal === 'change') {
        updateAnswer({
          field: key,
          value: value as string,
        });
      } else if (typeVal === 'track') {
        handleTrackEvent(e, value, key);
      }
    } else if (key === 'sentiment') {
      updateAnswer({ field: key, value: Number(value) }, (produced) =>
        handleTrackEvent(e, value, key, produced)
      );
    } else if (key === 'competition') {
      updateAnswer({ field: key, value: value as string }, (produced) =>
        handleTrackEvent(e, value, key, produced)
      );
    } else if (key === 'confirmation') {
      updateAnswer({ field: key, value: value as boolean }, (produced) =>
        handleTrackEvent(e, value, key, produced)
      );
    } else if (key === 'reason_for_leaving') {
      // Selecting a reason also clears `reason_other` in the same React event,
      // so the committed survey no longer holds the text this payload has
      // always carried. Report the state this action produced instead.
      updateReasonsForLeaving(
        key,
        value as string,
        tier!,
        currentTier!,
        (produced) => handleTrackEvent(e, value, key, produced)
      );
    }
  };

  // Clears other text value
  // when used in the reason_for_leaving questions surveySection.requires.multi_row_survey
  const clearCompetitionOtherText: CompetitorOtherTextClearHandler = (cb) => {
    updateAnswer({ field: 'competition_other', value: undefined }, () => {
      if (cb) {
        cb();
      }
    });
  };

  // Reason for leaving other value
  const handleReasonOtherText: ReasonOtherHandler = (
    value,
    name,
    tier,
    event,
    type = { type: 'change' }
  ) => {
    // update reasons for leaving array with other selected
    // add in reason_other in the array to hold the value that is typed
    if (_.get(type, 'type') === 'track') {
      handleTrackEvent(event, value, name);
    } else if (_.get(type, 'type') === 'change') {
      updateAnswer({ field: name, value });
    }
  };

  // Clears reason for leaving other value
  const clearReasonOtherText: ReasonOtherTextClearHandler = (cb) => {
    updateAnswer({ field: 'reason_other', value: undefined }, () => {
      if (cb) {
        cb();
      }
    });
  };

  // Track any entered information throughout the survey
  const handleAnyInformation: ModalHandleAnyInfo = ({ modal, message }) => {
    updateAnswer({
      field: 'responses',
      modalName: modal,
      value: message,
    });
  };

  // set the state of the upcoming modal
  const nextStep: NextStepHandler = (modalKey) => {
    const { cards } = derivedRef.current;
    const { modal: currentModal } = sessionStateRef.current;

    if (modalKey === 'confirm_cancel') {
      if (currentModal?.id === CANCEL_WITH_REFUND_MODAL_ID) {
        dispatchSession({ type: 'markRefundModalShown' });
      } else if (showRefundModalIfNeeded()) {
        return;
      }
    }

    // A miss leaves `modal` undefined, which the update effect reports as an
    // unconfigured action. The empty-object sentinel for "no key at all" is the
    // class component's historical value and is deliberately not nil.
    dispatchSession({
      type: 'selectModal',
      modal: modalKey
        ? (cards.get(modalKey) as RestModalCard | undefined)
        : ({} as RestModalCard),
    });
  };

  // shows the modal
  const continueFn = () => {
    dispatchSession({ type: 'openModal' });
  };

  // closes modal
  const nevermind: ModalOnClose = (reason) => {
    dispatchSession({ type: 'closeModal', reason });
  };

  const showLoaderOverlay = () => {
    if (contextRef.current.isPreview) {
      return;
    }
    dispatchSession({ type: 'showFullLoader' });
  };

  const directFromSurveyCancel: DirectFromSurveyCancelHandler = (action) => {
    if (showRefundModalIfNeeded()) {
      return;
    }

    // loader locks page while submission occuring
    showLoaderOverlay();
    const { cardsByTemplate } = derivedRef.current;
    // Leave the loader up when the page leaves.
    executeEvents(
      createDirectCancelQueue({
        experience: propsRef.current,
        survey: currentSurvey(),
        cardVersion: cardsByTemplate.get('multi_row_survey')?.version,
        action,
      })
    );
  };

  /**
   * `componentDidMount`, including its `componentWillUnmount` cleanup. Layout
   * rather than passive so it still runs before paint, as the class did.
   */
  useLayoutEffect(() => {
    unmountedRef.current = false;

    const { layout, name, id, session, config } = fullInfo;

    let storage = localState;
    if (!storage) {
      storage = new FunnelLocalState({ id, name, session }, isPreview);
      setLocalState(storage);
    }

    const savedState = storage.loadSavedState();

    if (savedState) {
      const { survey: savedSurvey, ...savedUi } = savedState;
      if (savedSurvey) answerSession.replaceAnswers(savedSurvey);
      dispatchSession({ type: 'hydrateSavedState', savedState: savedUi });
    }

    // Deliberately the render's state and not `sessionStateRef`: the class read
    // `this.state` here, which the hydrate above had not committed into yet, so
    // this writes the pre-hydration survey and the update effect re-saves the
    // hydrated one.
    storage.saveState({ ...sessionState, survey: answers });

    // Track page_load
    if (!isPricingPage && !pageLoadedRef.current) {
      pageLoadedRef.current = true;
      executeEvents({
        events: [createPageLoadedEvent({ experience: fullInfo, survey })],
      });
    }

    // Add any custom fonts
    embedFont({ layout });

    prefetchImages({ layout });

    // Check for page loaded modal
    if (config?.page_load_modal && !isBrandingPage) {
      const { page_load_modal } = config;
      let launchModal;
      if (page_load_modal.card_name) {
        launchModal = layout.cards.find(
          (cd) => cd.name === page_load_modal.card_name
        );
      } else {
        launchModal = layout.cards.find(
          (cd) => cd.id === page_load_modal.card_id
        );
      }
      if (!_.isNil(launchModal)) {
        const modalName = launchModal.name;
        // if delay, then delay by X ms
        const delay = _.get(page_load_modal, 'delay', 0);
        pageLoadModalTimerRef.current = setTimeout(() => {
          nextStep(modalName);
          continueFn();
        }, delay);
      } else {
        const error = `Initial page load modal could not be found. Please configure this experience correctly. \nCONFIG: ${JSON.stringify(
          page_load_modal
        )}`;
        console.warn(error);
      }
    }

    return () => {
      unmountedRef.current = true;
      clearTimeout(pageLoadModalTimerRef.current);
      clearTimeout(opaqueLoaderTimerRef.current);

      // A StrictMode remount replays this effect, and the update effect below
      // would otherwise treat that replay as a real update. The class never did
      // -- StrictMode double-invokes mounting, not updating -- so the first-run
      // guard is armed again here.
      isFirstCommitRef.current = true;
    };
    // Mount-only by construction, as `componentDidMount` was. The values it
    // closes over are the first render's deliberately -- the pre-hydration save
    // depends on reading state the hydrate above has not committed into -- and
    // anything that must be current when it fires goes through a ref instead.
    // Listing the dependencies would re-emit `page_loaded` and re-initialize
    // storage on every prop change.
  }, []);

  /**
   * `componentDidUpdate`. No dependency array, so it runs after every commit,
   * and the first is skipped: on mount there is no previous commit to compare
   * against, and running the body would emit modal events against itself.
   */
  useLayoutEffect(() => {
    if (isFirstCommitRef.current) {
      isFirstCommitRef.current = false;
      return;
    }

    const previous = previousCommitRef.current;
    previousCommitRef.current = {
      layoutCards: fullInfo.layout.cards,
      modal: sessionState.modal,
      showModal: sessionState.showModal,
    };

    const { isLifecycle, layout } = fullInfo;
    const { closeModalReason, modal, showModal } = sessionState;

    if (isPreview && layout.cards !== previous.layoutCards) {
      answerSession.replaceAnswers(derived.survey);
    }

    if (isPricingPage) {
      return;
    }
    // Save current state to LS
    localState?.saveState({ ...sessionState, survey: answers });

    // Send a track event for the following on a component update
    // modal_opened
    // modal_closed
    if (_.isObject(modal) && _.isObject(previous.modal)) {
      const prevModalUndefined = !previous.modal.name || !previous.showModal;

      if (prevModalUndefined && showModal) {
        executeEvents({
          events: [
            createModalOpenedEvent({
              experience: fullInfo,
              survey,
              modal,
            }),
          ],
        });
      }

      if (
        !prevModalUndefined &&
        !showModal &&
        modal.name !== previous.modal.name
      ) {
        const modalCtas = collectForwards(previous.modal);
        const modalCtasAreActionable = modalCtas.some((x) =>
          RestModalActionsAccepts.includes(x.value.action)
        );
        const isSuccessModal =
          previous.modal.template === RestModalTemplates.modal_email_success;
        const addRejectedOfferEvt =
          isLifecycle && modalCtasAreActionable && !isSuccessModal;
        executeEvents({
          events: createModalClosedEvents({
            experience: fullInfo,
            survey,
            modal,
            previousModal: previous.modal,
            includeRejectedOffer:
              !!addRejectedOfferEvt && closeModalReason === 'x',
          }),
        });
      }
    } else if (showModal && _.isNil(modal)) {
      console.error(
        '[BRIGHTBACK EXIT FUNNEL] No modal defined for this action. Check your configuration.'
      );
      dispatchSession({ type: 'hideUnconfiguredModal' });
    }
  });

  /**
   * Stands in for the second argument of `setState`, so it has to run after the
   * update effect above just as those callbacks ran after `componentDidUpdate`.
   *
   * The queue is taken and cleared before anything runs, because a callback is
   * free to dispatch again -- clearing competition text dispatches the
   * competition field from its own callback -- and that has to be queued for
   * the next commit rather than joining the flush already in progress.
   */
  useLayoutEffect(() => {
    if (pendingCallbacksRef.current.length === 0) {
      return;
    }

    const queued = pendingCallbacksRef.current;
    pendingCallbacksRef.current = [];
    queued.forEach((run) => run());
  });

  const { showModal, showLoader, showOpaqueLoader, modal } = sessionState;

  return (
    <FunnelView
      fullInfo={fullInfo}
      derived={derived}
      survey={survey}
      showModal={showModal}
      showLoader={showLoader}
      showOpaqueLoader={showOpaqueLoader}
      modal={modal}
      localState={localState}
      executeEvents={executeEvents}
      nextStep={nextStep}
      nevermind={nevermind}
      continueFn={continueFn}
      handleResponseMulti={handleResponseMulti}
      handleReasonOtherText={handleReasonOtherText}
      clearReasonOtherText={clearReasonOtherText}
      updateCompetitionOtherText={updateCompetitionOtherText}
      clearCompetitionOtherText={clearCompetitionOtherText}
      handleBackwards={handleBackwards}
      directFromSurveyCancel={directFromSurveyCancel}
      showLoaderOverlay={showLoaderOverlay}
      handleAnyInformation={handleAnyInformation}
    />
  );
};

export default Funnel;
